// EvidenceTab — generic evidence list with upload dialog, download, and archive.
// Reused by ControlEvidenceTab, ActionEvidenceTab, and EventEvidenceTab.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Paperclip, Archive, Eye, EyeOff, FileText, Calendar, User, Upload, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { UploadEvidenceDialog } from './UploadEvidenceDialog'

// --- Shared evidence item type ---

export interface EvidenceItem {
  id: string
  file_id: string
  file_name: string
  file_size_bytes: number
  mime_type: string
  download_url: string
  evidence_tag_id: string
  tag_name: string
  comment?: string | null
  is_archived: boolean
  archived_by?: string | null
  archived_at?: string | null
  uploaded_by: string
  uploaded_by_name: string
  uploaded_at: string
}

// --- Props ---

/** Parameters for uploading evidence via multipart form. */
export interface UploadEvidenceParams {
  file: File
  evidenceTagId: string
  comment?: string
}

export interface EvidenceTabProps {
  /** TanStack Query key for the evidence list. */
  queryKey: readonly unknown[]
  /** Function to fetch evidence items. */
  fetchFn: (includeArchived: boolean) => Promise<EvidenceItem[]>
  /** Function to upload a file as evidence (multipart). */
  uploadFn: (params: UploadEvidenceParams) => Promise<EvidenceItem>
  /** Function to download a file by its download URL (with auth). */
  downloadFn: (url: string) => Promise<Response>
  /** Function to archive a single evidence item. */
  archiveFn: (evidenceId: string) => Promise<unknown>
  /** Noun for empty-state message, e.g. "control", "action" or "event". */
  entityNoun: string
}

// --- Helpers ---

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// --- Component ---

export function EvidenceTab({ queryKey, fetchFn, uploadFn, downloadFn, archiveFn, entityNoun }: EvidenceTabProps) {
  const queryClient = useQueryClient()
  const [showArchived, setShowArchived] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)

  const {
    data: evidence,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...queryKey, { showArchived }],
    queryFn: () => fetchFn(showArchived),
  })

  const archiveMutation = useMutation({
    mutationFn: (evidenceId: string) => archiveFn(evidenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load evidence"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  const activeEvidence = evidence?.filter((e) => !e.is_archived) ?? []
  const archivedEvidence = evidence?.filter((e) => e.is_archived) ?? []
  const displayEvidence = showArchived ? evidence ?? [] : activeEvidence

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {activeEvidence.length} evidence item{activeEvidence.length !== 1 ? 's' : ''}
          {archivedEvidence.length > 0 && (
            <span className="text-muted-foreground/60">
              {' '}
              ({archivedEvidence.length} archived)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {archivedEvidence.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setShowArchived(!showArchived)}
            >
              {showArchived ? (
                <>
                  <EyeOff className="mr-1 h-3 w-3" /> Hide archived
                </>
              ) : (
                <>
                  <Eye className="mr-1 h-3 w-3" /> Show archived
                </>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setUploadOpen(true)}
          >
            <Upload className="mr-1 h-3.5 w-3.5" />
            Attach Evidence
          </Button>
        </div>
      </div>

      {/* Evidence list */}
      {displayEvidence.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <Paperclip className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm">No evidence attached to this {entityNoun}.</p>
            <p className="text-xs text-muted-foreground/60">
              Click &ldquo;Attach Evidence&rdquo; to upload a file.
            </p>
          </div>
        </div>
      ) : (
        <div className="divide-y rounded-lg border">
          {displayEvidence.map((item) => (
            <EvidenceRow
              key={item.id}
              item={item}
              onDownload={downloadFn}
              onArchive={() => archiveMutation.mutate(item.id)}
              isArchiving={archiveMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Upload dialog */}
      <UploadEvidenceDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        uploadFn={uploadFn}
        queryKey={queryKey}
      />
    </div>
  )
}

// --- Row ---

function EvidenceRow({
  item,
  onDownload,
  onArchive,
  isArchiving,
}: {
  item: EvidenceItem
  onDownload: (url: string) => Promise<Response>
  onArchive: () => void
  isArchiving: boolean
}) {
  const [isDownloading, setIsDownloading] = useState(false)

  // TD-001: Uses blob URLs because download endpoint requires auth headers.
  // Plain <a href> links in new tabs cannot attach Authorization headers.
  // Will be replaced with signed URLs when backend supports them.
  async function handleOpen() {
    if (isDownloading) return
    setIsDownloading(true)
    try {
      const response = await onDownload(item.download_url)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const win = window.open(blobUrl, '_blank')
      // Revoke after a delay to allow the new tab to load the blob.
      // Without this, large files may fail to render in the new tab.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
      if (!win) {
        // Popup blocked — fall back to download
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = item.file_name
        a.click()
      }
    } catch {
      // Silently fail — user can retry
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div
      className={cn(
        'flex items-center gap-4 px-4 py-3',
        item.is_archived && 'opacity-60',
      )}
    >
      {/* File icon + name */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <button
            type="button"
            onClick={handleOpen}
            disabled={isDownloading}
            className="truncate text-sm font-medium text-foreground underline-offset-2 hover:underline disabled:opacity-50"
            title={`Open ${item.file_name}`}
          >
            {isDownloading ? 'Opening...' : item.file_name}
          </button>
          {item.is_archived && (
            <Badge variant="outline" className="px-1 py-0 text-xs text-muted-foreground">
              Archived
            </Badge>
          )}
        </div>
        <div className="ml-6 mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
          <span>{item.tag_name}</span>
          <span>{formatFileSize(item.file_size_bytes)}</span>
          {item.comment && <span className="truncate italic">{item.comment}</span>}
        </div>
      </div>

      {/* Uploaded by */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <User className="h-3 w-3" />
        <span className="max-w-[100px] truncate">{item.uploaded_by_name}</span>
      </div>

      {/* Uploaded date */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground lg:flex">
        <Calendar className="h-3 w-3" />
        <span>{new Date(item.uploaded_at).toLocaleDateString()}</span>
      </div>

      {/* Open / Archive buttons */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={handleOpen}
          disabled={isDownloading}
          title="Open file in new tab"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
        {!item.is_archived && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={onArchive}
            disabled={isArchiving}
            title="Archive this evidence"
          >
            <Archive className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  )
}
